import React, { useState, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { variant, borderColor } from 'styled-system';
import styled from 'styled-components';
import Styled from '../../utils/Styled';
import { Variant } from '../../utils/constants';
import { useWindowSize } from '../../utils';
import Wrapper from '../Wrapper';
import Text from '../Text/Text';
import Row from '../Container/Row';
import { CaptionText } from '../Text/Heading';
import Icon from '../Icon';
import PickerItemsDesktop from './PickerItems.desktop';
import PickerItemsMobile from './PickerItems.mobile';
import PickerItemsModalMobile from './PickerItemsModal.mobile';

const RowWrapper = styled(Row)`
  ${borderColor};
  cursor: pointer;
  outline: none;
`;
const HiddenSelect = styled.select`
  position: absolute;
  top: -10px;
  opacity: 0;
`;

const Box = Styled(
  RowWrapper,
  {
    position: 'relative',
    width: 1,
    border: '1px solid',
    borderColor: 'greyLight',
    borderRadius: 4,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  variant({
    variants: {
      [Variant.Error]: {
        borderColor: 'tertiaryOrange',
      },
    },
  })
);

Box.displayName = 'PicketSelectBox';

const IconBox = Styled(Wrapper, {
  position: 'absolute',
  display: 'flex',
  right: 11,
  alignItems: 'center',
});

const PlaceHolderText = Styled(Text, {
  color: 'greyDark',
  opacity: 0.5,
  lineHeight: '20px',
  fontWeight: 400,
});

const ValueText = Styled(Text, {
  color: 'darkBlack',
  fontWeight: 400,
  lineHeight: '20px',
  margin: 0,
  padding: 0,
  fontSize: 16,
});

const LabelText = Styled(CaptionText, {
  opacity: 0.5,
  numberOfLines: 1,
  position: ['absolute', 'relative'],
  padding: 0,
  paddingRight: [10, 0],
});

const PickerIcon = ({ showPicker }) => (
  <IconBox>
    {showPicker ? (
      <Icon icon="ArrowDropUpIcon" />
    ) : (
      <Icon icon="ArrowDropDownIcon" />
    )}
  </IconBox>
);

PickerIcon.propTypes = {
  showPicker: PropTypes.bool,
};

PickerIcon.displayName = 'PickerIcon';

const InnerBox = ({ paddingY = 14, ...props }) => (
  <Wrapper width={1} paddingX={4} paddingY={paddingY} {...props} />
);

InnerBox.propTypes = {
  paddingY: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const getItem = (propValue, items) =>
  items.find(({ value }) => value === propValue);

const PickerSelect = ({
  items = [],
  label,
  value,
  onChange,
  onFocus,
  onClick,
  placeholder,
  showPlaceholderOption,
  variant,
  renderItemDisplayValue,
  renderDisplayValue,
  renderActiveLabel,
  boxProps,
  innerBoxProps,
  modalProps,
  showRightText,
  showModalMobile,
  modalMobileProps,
  onOpenPickingSelectModal,
  onClosePickingSelectModal,
  move,
}) => {
  const { isMobileView } = useWindowSize();
  const [showPicker, setShowPicker] = useState(false);

  const togglePicker = event => {
    event.preventDefault();
    event.stopPropagation();
    setShowPicker(!showPicker);
    window.addEventListener('click', closeAllSelect);
    if (!showPicker) onOpenPickingSelectModal && onOpenPickingSelectModal();
    else onClosePickingSelectModal && onClosePickingSelectModal();
  };

  const togglePickerMobile = event => {
    event.preventDefault();
    event.stopPropagation();
    setShowPicker(true);
    if (onOpenPickingSelectModal) onOpenPickingSelectModal();
  };

  useEffect(() => {
    if (showPicker) {
      onClick();
      onFocus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPicker]);

  const handleValueChange = (value, index) => {
    setShowPicker(false);
    if (onClosePickingSelectModal) onClosePickingSelectModal();
    const item = index >= 0 ? items[index] : {};
    onChange(item.value, item);
  };

  const handleValueChangeOnDesktop = (value, index) => {
    handleValueChange(value, index);
    window.removeEventListener('click', closeAllSelect);
  };

  const closeAllSelect = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!showPicker) {
      setShowPicker(false);
      if (onClosePickingSelectModal) onClosePickingSelectModal();
    }
    window.removeEventListener('click', closeAllSelect);
  };

  const handleBlur = event => {
    event.preventDefault();
    event.stopPropagation();
    setShowPicker(false);
    if (onClosePickingSelectModal) onClosePickingSelectModal();
  };

  const renderItem = valueTextProps => {
    const selectedItem = getItem(value, items);
    if (renderItemDisplayValue && selectedItem) {
      return renderItemDisplayValue(
        selectedItem.value,
        selectedItem,
        valueTextProps
      );
    }
    const label = selectedItem ? selectedItem.label : '';
    return (
      <ValueText {...valueTextProps}>
        {selectedItem && renderDisplayValue
          ? renderDisplayValue(selectedItem.value, selectedItem)
          : label}
      </ValueText>
    );
  };

  const renderLabel = () => {
    if (renderActiveLabel) {
      const selectedItem = getItem(value, items);
      return selectedItem
        ? renderActiveLabel(selectedItem.value, selectedItem)
        : label;
    }
    return label;
  };

  const notEmptyValue = !(value == null);

  return (
    <Fragment>
      <Wrapper {...boxProps}>
        <Box
          variant={variant}
          display={['none', 'flex']}
          borderColor={
            showPicker ? (move && 'secondaryMove') || 'secondary' : 'greyLight'
          }
          onClick={togglePicker}
        >
          <InnerBox
            paddingY={notEmptyValue ? '6px' : '14px'}
            {...innerBoxProps}
          >
            {notEmptyValue ? (
              <Fragment>
                <LabelText>{renderLabel()}</LabelText>
                {renderItem()}
              </Fragment>
            ) : (
              <PlaceHolderText numberOfLines={1}>{placeholder}</PlaceHolderText>
            )}
          </InnerBox>
          <PickerIcon showPicker={showPicker} />
        </Box>
        {isMobileView && (
          <Fragment>
            <Box
              variant={variant}
              borderColor={
                showPicker
                  ? (move && 'secondaryMove') || 'secondaryMove'
                  : 'greyLight'
              }
              display={'flex'}
              onClick={
                showModalMobile ? togglePickerMobile : Function.prototype
              }
            >
              <PickerIcon showPicker={showPicker} />
              <InnerBox
                paddingY={notEmptyValue ? '6px' : '14px'}
                {...innerBoxProps}
              >
                {notEmptyValue ? (
                  <Fragment>
                    <LabelText>{renderLabel()}</LabelText>
                    {renderItem({
                      whiteSpace: 'nowrap',
                      position: showModalMobile ? 'relative' : 'absolute',
                      padding: 0,
                      paddingTop: 16,
                    })}
                  </Fragment>
                ) : (
                  <PlaceHolderText
                    position={showModalMobile ? 'relative' : 'absolute'}
                    numberOfLines={1}
                    marginRight={48}
                  >
                    {placeholder}
                  </PlaceHolderText>
                )}

                {showModalMobile ? (
                  <PickerItemsModalMobile
                    items={items}
                    onClose={handleBlur}
                    onValueChange={handleValueChange}
                    value={value}
                    isOpen={showPicker}
                    showRightText={showRightText}
                    variant={value ? Variant.Success : Variant.Default}
                    modalMobileProps={modalMobileProps}
                  />
                ) : (
                  <PickerItemsMobile
                    placeholder={placeholder}
                    items={items}
                    label={renderLabel()}
                    onClick={togglePickerMobile}
                    onBlur={handleBlur}
                    onValueChange={handleValueChange}
                    value={value}
                    showPicker={showPicker}
                    showPlaceholderOption={showPlaceholderOption}
                    variant={value ? Variant.Success : Variant.Default}
                  />
                )}
              </InnerBox>
            </Box>
          </Fragment>
        )}
      </Wrapper>

      <Wrapper
        display={['none', 'block']}
        position="relative"
        width={1}
        {...modalProps}
      >
        {!isMobileView && (
          <Fragment>
            <HiddenSelect onFocus={togglePicker} />
            <PickerItemsDesktop
              placeholder={placeholder}
              showPlaceholderOption={showPlaceholderOption}
              items={items}
              onValueChange={handleValueChangeOnDesktop}
              value={value}
              showPicker={showPicker}
              showRightText={showRightText}
            />
          </Fragment>
        )}
      </Wrapper>
    </Fragment>
  );
};

PickerSelect.propTypes = {
  items: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boxProps: PropTypes.object,
  innerBoxProps: PropTypes.object,
  modalProps: PropTypes.object,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  showPlaceholderOption: PropTypes.bool,
  variant: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  renderItemDisplayValue: PropTypes.func,
  renderDisplayValue: PropTypes.func,
  renderActiveLabel: PropTypes.func,
  showRightText: PropTypes.bool,
  showModalMobile: PropTypes.bool,
  modalMobileProps: PropTypes.object,
  onOpenPickingSelectModal: PropTypes.func,
  onClosePickingSelectModal: PropTypes.func,
  move: PropTypes.bool,
};

PickerSelect.defaultProps = {
  onChange: () => {},
  onFocus: () => {},
};
export default PickerSelect;
